import Marquee from 'react-fast-marquee';
import { sectionStyles, buildSrcSet } from '../utils';
import SectionDivider from './SectionDivider';

export default function ImageMarqueeSection({ block = {}, primaryColor, secondaryColor, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const height = config.height || 'medium';
    const grayscale = config.grayscale || false;

    // Get images from folder or content
    const folderId = content.folder_id;
    const _allImages = folderId && mediaFolders[folderId] ? mediaFolders[folderId] : [];
    const _sel = content.selected_file_ids;
    const folderImages = _sel?.length > 0 ? _allImages.filter(f => _sel.includes(f.id)) : _allImages;
    const imageFiles = folderImages.length > 0 ? folderImages : [];
    const images = imageFiles.length > 0
        ? imageFiles.map(img => typeof img === 'string' ? { url: img } : img)
        : (content.images || []).map(u => ({ url: u }));

    if (images.length === 0) {
        return (
            <section className={`py-12 relative ${s.radiusClass} ${s.shadowClass}`} style={s.bgStyle}>
                <div className="text-center text-sm" style={{ color: s.dark ? 'rgba(255,255,255,0.4)' : '#9ca3af' }}>Selecciona una carpeta de imagenes para el marquee</div>
            </section>
        );
    }

    const hClass = height === 'small' ? 'h-32 md:h-40' : height === 'large' ? 'h-64 md:h-80' : 'h-48 md:h-60';
    const direction = config.direction === 'right' ? 'right' : 'left';
    const speed = config.speed === 'fast' ? 80 : config.speed === 'slow' ? 20 : 40;
    const variant = config.variant || 'default';

    // ── Dual-rows variant ──
    if (variant === 'dual-rows') {
        const mid = Math.ceil(images.length / 2);
        const row1 = images.slice(0, mid);
        const row2 = images.slice(mid);
        return (
            <section className={`relative overflow-hidden ${s.radiusClass} ${s.shadowClass}`} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div className="py-4 space-y-3">
                    <div className={hClass}>
                        <Marquee speed={speed} pauseOnHover gradient={false} direction="left" className="h-full">
                            {row1.map((img, i) => (
                                <div key={i} className={`h-full flex-shrink-0 rounded-xl overflow-hidden mx-2 ${grayscale ? 'grayscale hover:grayscale-0 transition-all duration-500' : ''}`}>
                                    <img src={img.image_url || img.url || ''} alt="" className="h-full w-auto object-cover rounded-xl" style={{ minWidth: '200px', maxWidth: '400px' }} srcSet={buildSrcSet(img)} sizes="(max-width:768px) 80vw, 400px" loading="lazy" decoding="async" />
                                </div>
                            ))}
                        </Marquee>
                    </div>
                    <div className={hClass}>
                        <Marquee speed={speed} pauseOnHover gradient={false} direction="right" className="h-full">
                            {(row2.length > 0 ? row2 : row1).map((img, i) => (
                                <div key={i} className={`h-full flex-shrink-0 rounded-xl overflow-hidden mx-2 ${grayscale ? 'grayscale hover:grayscale-0 transition-all duration-500' : ''}`}>
                                    <img src={img.image_url || img.url || ''} alt="" className="h-full w-auto object-cover rounded-xl" style={{ minWidth: '200px', maxWidth: '400px' }} srcSet={buildSrcSet(img)} sizes="(max-width:768px) 80vw, 400px" loading="lazy" decoding="async" />
                                </div>
                            ))}
                        </Marquee>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent" position="bottom" config={config}/>
            </section>
        );
    }

    // ── Logo-grid variant ──
    if (variant === 'logo-grid') {
        return (
            <section className={`relative overflow-hidden ${s.radiusClass} ${s.shadowClass}`} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div className="py-10 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
                        {images.map((img, i) => (
                            <div key={i} className="flex items-center justify-center p-6 rounded-xl transition-all duration-500 grayscale hover:grayscale-0 hover:scale-105 hover:shadow-lg" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#f9fafb' }}>
                                <img src={img.image_url || img.url || ''} alt="" className="max-h-16 w-auto object-contain" srcSet={buildSrcSet(img)} sizes="200px" loading="lazy" decoding="async" />
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent" position="bottom" config={config}/>
            </section>
        );
    }

    // ── Default variant ──
    return (
        <section className={`relative overflow-hidden ${s.radiusClass} ${s.shadowClass}`} style={s.bgStyle}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div className={`py-4 overflow-hidden ${hClass}`}>
                <Marquee speed={speed} pauseOnHover gradient={false} direction={direction} className="h-full">
                    {images.map((img, i) => (
                        <div key={i} className={`h-full flex-shrink-0 rounded-xl overflow-hidden mx-2 ${grayscale ? 'grayscale hover:grayscale-0 transition-all duration-500' : ''}`}>
                            <img
                                src={img.image_url || img.url || ''}
                                alt=""
                                className="h-full w-auto object-cover rounded-xl"
                                style={{ minWidth: '200px', maxWidth: '400px' }}
                                srcSet={buildSrcSet(img)}
                                sizes="(max-width:768px) 80vw, 400px"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    ))}
                </Marquee>
            </div>
            <SectionDivider type={config.divider_bottom} color="transparent" position="bottom"  config={config}/>
        </section>
    );
}
